import { useCallback, useEffect, useState } from "react";
import DashboardShell from "@/components/layout/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { VetBadge, ConsultationDialog, MODE_LABEL, vfmt } from "@/components/vet/vetShared";
import { Siren, UserCog, CalendarCheck2 } from "lucide-react";

function RescheduleDialog({ appt, open, setOpen, onDone }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/vet/me/appointments/${appt.appointment_id}/respond`, { action: "reschedule", date, time });
      toast.success("Reschedule proposed"); setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!appt) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm" data-testid="vet-resched-dialog">
        <DialogHeader><DialogTitle>Reschedule Appointment</DialogTitle></DialogHeader>
        <Input type="date" className="am-input" data-testid="vet-resched-date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Input type="time" className="am-input" data-testid="vet-resched-time" value={time} onChange={(e) => setTime(e.target.value)} />
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="am-btn-primary" disabled={!date || busy} data-testid="vet-resched-save" onClick={save}>Propose</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProfileDialog({ me, open, setOpen, onDone }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && me) setF({ ...me, areas_text: (me.service_areas || []).join(", ") }); }, [open, me]);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/vet/me", {
        email: f.email, qualification: f.qualification, specialization: f.specialization,
        experience_years: Number(f.experience_years || 0), clinic_name: f.clinic_name, address: f.address,
        service_areas: (f.areas_text || "").split(",").map((s) => s.trim()).filter(Boolean),
        consultation_fee: Number(f.consultation_fee || 0), home_visit_fee: Number(f.home_visit_fee || 0),
        emergency_fee: Number(f.emergency_fee || 0), emergency_available: !!f.emergency_available,
        online_available: !!f.online_available, home_visit_available: !!f.home_visit_available,
        availability_note: f.availability_note || "",
      });
      toast.success("Profile updated"); setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const F = ({ k, label, type = "text" }) => (
    <div>
      <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</Label>
      <Input type={type} className="am-input mt-1" data-testid={`vp-${k}`} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="vet-profile-dialog">
        <DialogHeader><DialogTitle>My Profile & Availability</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F k="qualification" label="Qualification" /><F k="specialization" label="Specialization" />
          <F k="experience_years" label="Experience (yrs)" type="number" /><F k="clinic_name" label="Clinic / Hospital" />
          <F k="consultation_fee" label="Consultation Fee ₹" type="number" /><F k="home_visit_fee" label="Home Visit Fee ₹" type="number" />
          <F k="emergency_fee" label="Emergency Fee ₹" type="number" /><F k="email" label="Email" />
        </div>
        <F k="address" label="Address" />
        <F k="areas_text" label="Service Areas (comma separated)" />
        <F k="availability_note" label="Availability (e.g. Mon-Sat 9am-6pm)" />
        <div className="flex flex-wrap gap-5 pt-1">
          {[["emergency_available", "Emergency Service"], ["home_visit_available", "Home Visits"], ["online_available", "Online Consultation"]].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm font-semibold">
              <Switch checked={!!f[k]} onCheckedChange={(v) => setF({ ...f, [k]: v })} data-testid={`vp-${k}`} /> {label}
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="am-btn-primary" disabled={busy} data-testid="vp-save" onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function VetDashboard() {
  const [me, setMe] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [appts, setAppts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [consultFor, setConsultFor] = useState(null);
  const [reschedFor, setReschedFor] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    api.get("/vet/me").then((r) => setMe(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Failed to load profile"));
    api.get("/vet/me/dashboard").then((r) => setKpi(r.data)).catch(() => {});
    api.get("/vet/me/appointments").then((r) => setAppts(r.data)).catch(() => {});
    api.get("/vet/me/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/vet/me/followups").then((r) => setFollowups(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 25000);
    return () => clearInterval(id);
  }, [refresh]);

  const respond = async (appt, action) => {
    setBusy(true);
    try {
      await api.post(`/vet/me/appointments/${appt.appointment_id}/respond`, { action });
      toast.success(`Appointment ${action}ed`); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const kpis = kpi && [
    ["Today", kpi.today_appointments], ["Pending Requests", kpi.pending_requests],
    ["Upcoming", kpi.upcoming], ["Completed", kpi.completed],
    ["Emergency", kpi.emergency_requests], ["Earnings (paid)", vfmt(kpi.total_earnings)],
    ["Pending Payout", vfmt(kpi.pending_payments)], ["Follow-ups", kpi.pending_followups],
  ];
  const active = appts.filter((a) => !["REJECTED", "CANCELLED", "COMPLETED"].includes(a.status));
  const done = appts.filter((a) => ["COMPLETED"].includes(a.status));

  return (
    <DashboardShell title="Vet Dashboard" subtitle={me ? `${me.name}${me.verification_status !== "VERIFIED" ? " · awaiting admin verification" : ""}` : ""}
      action={<Button variant="outline" className="rounded-full" data-testid="vet-profile-btn" onClick={() => setProfileOpen(true)}><UserCog className="w-4 h-4 mr-1.5" /> My Profile</Button>}>
      <div className="space-y-6" data-testid="vet-dashboard">
        {me && me.verification_status !== "VERIFIED" && (
          <div className="rounded-xl bg-amber-100 text-amber-900 text-sm font-semibold px-4 py-3" data-testid="vet-unverified-note">
            Your profile is {me.verification_status}. Clients can book you only after admin verification.
          </div>
        )}
        {kpis && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="vet-kpis">
            {kpis.map(([label, value]) => (
              <div key={label} className="am-card p-4">
                <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
                <div className="font-display text-2xl font-extrabold mt-1">{value}</div>
              </div>
            ))}
          </div>
        )}

        <div className="am-card p-5 space-y-3" data-testid="vet-appointments">
          <h2 className="font-display text-lg font-bold flex items-center gap-2"><CalendarCheck2 className="w-5 h-5 text-primary" /> Appointments</h2>
          {active.length === 0 && <p className="text-sm text-muted-foreground">No active appointments.</p>}
          {active.map((a) => {
            const isEmg = a.is_emergency || a.mode === "emergency";
            return (
            <div key={a.appointment_id} className={`rounded-xl border p-3 ${isEmg ? "border-destructive bg-destructive/5" : "border-border"}`} data-testid={`vet-appt-${a.appointment_id}`}>
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="min-w-0">
                  <div className="font-bold text-sm flex items-center gap-2">
                    {isEmg && <Siren className="w-4 h-4 text-destructive" />}
                    {a.client_name} · {[a.animal_info?.type, a.animal_info?.breed].filter(Boolean).join(" ") || "animal"}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {MODE_LABEL[a.mode]} · {a.date} {a.time} · {vfmt(a.fee)}{a.location ? ` · ${a.location}` : ""}
                  </div>
                  <div className="text-xs mt-1"><b>Symptoms:</b> {a.symptoms}</div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <VetBadge status={a.status} />
                  {a.status === "REQUESTED" && (
                    <>
                      <Button size="sm" className="am-btn-primary rounded-full text-xs" disabled={busy} data-testid={`vet-accept-${a.appointment_id}`} onClick={() => respond(a, "accept")}>Accept</Button>
                      <Button size="sm" variant="outline" className="rounded-full text-xs text-destructive" disabled={busy} data-testid={`vet-reject-${a.appointment_id}`} onClick={() => respond(a, "reject")}>Reject</Button>
                    </>
                  )}
                  {["REQUESTED", "ACCEPTED", "CONFIRMED"].includes(a.status) && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs" disabled={busy} data-testid={`vet-resched-${a.appointment_id}`} onClick={() => setReschedFor(a)}>Reschedule</Button>
                  )}
                  {["ACCEPTED", "CONFIRMED", "RESCHEDULED", "IN_PROGRESS"].includes(a.status) && (
                    <Button size="sm" className="am-btn-primary rounded-full text-xs" disabled={busy} data-testid={`vet-consult-${a.appointment_id}`} onClick={() => setConsultFor(a)}>
                      Consult & Prescribe
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );})}
          {done.length > 0 && <p className="text-xs text-muted-foreground pt-1">{done.length} completed consultation(s) — see earnings below.</p>}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="am-card p-5" data-testid="vet-followups">
            <h2 className="font-display text-lg font-bold mb-3">Follow-ups</h2>
            {followups.filter((f) => f.status === "PENDING").length === 0 && <p className="text-sm text-muted-foreground">No pending follow-ups.</p>}
            {followups.filter((f) => f.status === "PENDING").map((f) => (
              <div key={f.followup_id} className="flex items-center justify-between gap-2 border-b last:border-0 py-2 text-sm">
                <div><b>{f.due_date}</b> · {[f.animal_info?.type, f.animal_info?.breed].filter(Boolean).join(" ")}<div className="text-xs text-muted-foreground">{f.note}</div></div>
                <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid={`vet-fu-done-${f.followup_id}`}
                  onClick={() => api.post(`/vet/me/followups/${f.followup_id}/status`, { status: "DONE" }).then(() => { toast.success("Follow-up done"); refresh(); })}>
                  Mark Done
                </Button>
              </div>
            ))}
          </div>
          <div className="am-card p-5" data-testid="vet-earnings">
            <h2 className="font-display text-lg font-bold mb-3">My Earnings</h2>
            {payments.length === 0 && <p className="text-sm text-muted-foreground">No consultations billed yet.</p>}
            {payments.map((p) => (
              <div key={p.payment_id} className="flex items-center justify-between gap-2 border-b last:border-0 py-2 text-sm">
                <div><b>{p.client_name}</b> · {MODE_LABEL[p.mode]}<div className="text-xs text-muted-foreground">fee {vfmt(p.fee)} − {p.commission_percent}% = payout <b>{vfmt(p.vet_payout)}</b></div></div>
                <VetBadge status={p.status} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <ConsultationDialog appt={consultFor} open={!!consultFor} setOpen={() => setConsultFor(null)} onDone={refresh} />
      <RescheduleDialog appt={reschedFor} open={!!reschedFor} setOpen={() => setReschedFor(null)} onDone={refresh} />
      <ProfileDialog me={me} open={profileOpen} setOpen={setProfileOpen} onDone={refresh} />
    </DashboardShell>
  );
}
